/**
 * The device id: the one value the agent WebSocket (`?device_id=`), the cart
 * and the wishlist (`X-Device-Id`) send to say which browser is talking. The
 * backend uses it for session-takeover bookkeeping (its API description), and
 * accepts any string.
 *
 * A device here is one browser profile on one machine — a web page cannot
 * read a hardware identifier, so Chrome and Safari on one laptop are two
 * devices under any scheme. Since 2026-10-26 the id is the **Firebase
 * Installation ID** (FID), as the backend team asked: a random id Firebase
 * creates in the browser and keeps in IndexedDB. It replaced a SHA-256 of
 * browser and screen properties, which was neither stable (a browser update,
 * another monitor or a new time zone changed it after a sign-out) nor unique
 * (identical phones and laptops hashed to the same value).
 *
 * Resolution, decided once per page load (see getOrbieeDeviceId):
 *   1. The fingerprint id an earlier version stored in localStorage, while
 *      the browser still has it. This is the gradual migration: sign-out
 *      clears localStorage (utils/auth.js), so each browser moves to the FID
 *      at its next sign-in, and a deploy never splits one browser into two
 *      devices (an open tab on the old bundle reads the same stored value).
 *      Drop this step to switch every browser at its next page load instead.
 *   2. The Firebase Installation ID. It survives sign-out with no help —
 *      sign-out clears localStorage, sessionStorage and cookies, never
 *      IndexedDB — and Firebase creates it inside one IndexedDB transaction,
 *      so two tabs opened at the same moment get the same id.
 *   3. The old fingerprint, for this page only and never stored. Reached
 *      only when Firebase fails or is slow (IndexedDB blocked, a hung open,
 *      missing config). Not storing it is deliberate: a stored fallback
 *      would pin the browser to the fingerprint after one slow moment. On a
 *      secure origin the fingerprint is deterministic, so a browser that
 *      always falls back still keeps one id.
 *
 * Contract every caller relies on: the promise never rejects, and every call
 * in one page load resolves to the same string. The socket is keyed on
 * user + device (orbieeChatTurn's getOrCreateSocket), so a value that changed
 * mid-page would tear down a live conversation. Not a credential: anyone can
 * send any header value.
 */
import { getInstallations, getId } from "firebase/installations";
import { firebaseApp } from "../lib/firebase";

/** Where the fingerprint id used to be cached. Read here, never written. */
export const LEGACY_DEVICE_ID_KEY = "orbiee_device_id";

/**
 * How long to wait for Firebase before falling back. Reading or creating the
 * id is local (an IndexedDB transaction, milliseconds); registration with
 * Google runs in the background and is not awaited. The wait only matters
 * when IndexedDB hangs, and sign-in awaits the socket, which awaits this.
 */
export const FIREBASE_DEVICE_ID_TIMEOUT_MS = 2000;

/**
 * Print the id and where it came from on every request for it — the socket
 * connect, each cart or wishlist call, each turn — so it can be checked in
 * the console of any build, deployed ones included (asked for 2026-10-26).
 * One line per request, so a busy page prints it often. Set to false to
 * silence it.
 */
const LOG_DEVICE_ID = true;

/** @type {Promise<string> | null} */
let deviceIdPromise = null;
/** Which step of resolveDeviceId supplied this page's id, for the log. */
let deviceIdSource = null;

/**
 * The device id for this page load. The first call decides it; every later
 * call — the socket connect, each cart or wishlist request, each turn —
 * gets the same promise, so Firebase is asked once per page.
 * @returns {Promise<string>} never rejects
 */
export function getOrbieeDeviceId() {
  if (!deviceIdPromise) deviceIdPromise = resolveDeviceId();
  if (LOG_DEVICE_ID) {
    // A side branch: callers still get the one shared promise, and it never
    // rejects, so this cannot raise an unhandled rejection.
    deviceIdPromise.then((id) => {
      console.log("[DeviceId]", id, "from", deviceIdSource);
    });
  }
  return deviceIdPromise;
}

async function resolveDeviceId() {
  const legacyId = readLegacyDeviceId();
  if (legacyId) return withSource("the old stored id", legacyId);

  const installationId = await readFirebaseInstallationId();
  if (installationId) return withSource("the Firebase Installation ID", installationId);

  return withSource("the fingerprint fallback", await computeDeviceFingerprint());
}

function readLegacyDeviceId() {
  try {
    const value = localStorage.getItem(LEGACY_DEVICE_ID_KEY);
    return typeof value === "string" && value ? value : null;
  } catch {
    // Storage blocked: behave as a browser that never had one.
    return null;
  }
}

async function readFirebaseInstallationId() {
  let timer;
  try {
    const timedOut = new Promise((resolve) => {
      timer = setTimeout(() => resolve(null), FIREBASE_DEVICE_ID_TIMEOUT_MS);
    });
    // getInstallations throws synchronously when the config lacks a field;
    // inside the try, that is a fallback, not a crash. A getId that settles
    // after the timeout won is still handled by the race.
    const id = await Promise.race([
      getId(getInstallations(firebaseApp)),
      timedOut,
    ]);
    if (typeof id === "string" && id) return id;
    console.warn("[DeviceId] Firebase Installation ID unavailable in time; using the fallback");
    return null;
  } catch (err) {
    console.warn("[DeviceId] Firebase Installation ID failed; using the fallback", err);
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * The previous device id, unchanged: a SHA-256 of browser and screen
 * properties formatted 8-4-4-4-12. Outside a secure context there is no
 * SubtleCrypto (nor randomUUID), so it is random. Never rejects.
 */
async function computeDeviceFingerprint() {
  try {
    const nav = typeof navigator !== "undefined" ? navigator : {};
    const scr = typeof window !== "undefined" ? window.screen || {} : {};
    const tz = (() => {
      try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return ""; }
    })();
    const raw = [
      nav.userAgent || "",
      nav.language || "",
      String(nav.hardwareConcurrency || ""),
      String(nav.deviceMemory || ""),
      String(nav.platform || ""),
      String(scr.width || ""),
      String(scr.height || ""),
      String(scr.colorDepth || ""),
      String(scr.pixelDepth || ""),
      tz,
    ].join("|");

    try {
      const buf = await crypto.subtle.digest(
        "SHA-256",
        new TextEncoder().encode(raw)
      );
      const hex = Array.from(new Uint8Array(buf))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
    } catch {
      return crypto.randomUUID?.() || randomFallbackId();
    }
  } catch {
    // No crypto object at all — still an id, never a rejection.
    return randomFallbackId();
  }
}

function randomFallbackId() {
  return `dev_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function withSource(source, id) {
  deviceIdSource = source;
  return id;
}
